import React, { useState, useEffect, useCallback, useMemo } from "react";
import { database } from "../firebaseConfig";
import { ref, update, onValue, push, set, remove, get } from "firebase/database";
import { getFunctions, httpsCallable } from "firebase/functions";
import ConfirmModal from "./ConfirmModal";
import AgentEditor from "./Agents/AgentEditor";
import AgentTemplatePicker from "./Agents/AgentTemplatePicker";
import { BLANK_AGENT, templateToDraft, ADHERENCE_LEVELS } from "./Agents/agentTemplates";
import agentsFromSnapshot from "./Agents/agentsFromSnapshot";
// Global voice-capture engine — 'inboxReplyCase' preset walks the user
// through 6 questions and returns a fully-drafted case (trigger, action,
// mood range, dos/donts, tag) that we drop into <AgentEditor /> for review.
import { useSpeakEngine } from "../speakEngine";
import "./AgentsSettings.css";

/**
 * AgentsSettings — the Inbox Reply pane under Intelligence.
 *
 * Rules live in RTDB at /World/{uid}/settings/agents. Every incoming email is
 * matched against their trigger descriptions by the backend
 * (functions/EmailAgentProcessor.js); the winning rule either drafts a reply,
 * stays silent, or just tags the mail.
 *
 * This component owns loading, saving and the list. The form itself lives in
 * AgentEditor so creating and editing can no longer drift apart, and the
 * template gallery lives in AgentTemplatePicker.
 */

const ACTION_META = {
  Reply: { label: 'Replies', icon: 'fa-pen-nib', tone: 'reply' },
  'No Reply': { label: 'Silent', icon: 'fa-circle-minus', tone: 'silent' },
  'Only Tag': { label: 'Tag only', icon: 'fa-tag', tone: 'tag' },
};

// RTDB hands arrays back as arrays when they were written dense, but as an
// object keyed by index if anything ever wrote a gap. Normalise both.
const toList = (value) => {
  const raw = Array.isArray(value)
    ? value
    : (value && typeof value === 'object' ? Object.values(value) : []);
  const seen = new Set();
  return raw
    .map((entry) => String(entry || '').trim())
    .filter((entry) => entry && !seen.has(entry) && seen.add(entry));
};

// Mirrors the positivity filter in the editor: the analyzer scores every reply
// from 1 to 10, and a case only runs inside its own slice of that scale.
const describeRating = (min, max) => {
  const lo = min || 1;
  const hi = max || 10;
  return lo <= 1 && hi >= 10 ? 'any rating' : `rating ${lo}–${hi}`;
};

const AgentsSettings = ({ userId }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null); // { kind: 'ok' | 'error', text }

  // The editor is open when `editor` is set. `id` is null for a new rule.
  // `initial` is kept so leaving can tell an untouched draft from real work.
  const [editor, setEditor] = useState(null); // { id, draft, initial }
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [pendingDiscard, setPendingDiscard] = useState(false);

  const [autoProcess, setAutoProcess] = useState(false);
  const [selectedTopics, setSelectedTopics] = useState({});
  const [customTopics, setCustomTopics] = useState({});
  const userTopics = useMemo(
    () => ({ ...selectedTopics, ...customTopics }),
    [selectedTopics, customTopics],
  );
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [eventTypesError, setEventTypesError] = useState("");

  const functions = getFunctions();
  const { openCapture } = useSpeakEngine();

  const flash = useCallback((kind, text, ms = 3500) => {
    setNotice({ kind, text });
    setTimeout(() => setNotice(null), ms);
  }, []);

  // ── Load rules ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return undefined;

    const agentsRef = ref(database, `/World/${userId}/settings/agents`);
    let generation = 0;
    const unsubscribe = onValue(agentsRef, (snapshot) => {
      const myGeneration = ++generation;
      (async () => {
        if (!snapshot.exists()) {
          if (myGeneration !== generation) return;
          setAgents([]);
          setLoading(false);
          return;
        }

        const list = agentsFromSnapshot(snapshot);

        // Tags used to live only under /aiTags. Rules created before the merge
        // still carry their label there, so fold it back in for display.
        try {
          const tagsSnapshot = await get(ref(database, `/World/${userId}/settings/aiTags`));
          if (myGeneration !== generation) return;
          if (tagsSnapshot.exists()) {
            const tags = tagsSnapshot.val();
            list.forEach((agent) => {
              const linked = tags[agent.id];
              if (linked && !agent.useTag) {
                agent.useTag = true;
                agent.tag = linked.tag || '';
                agent.tagColor = linked.color || '#1e40af';
                agent.tagIcon = linked.icon || '';
                agent.tagDescription = linked.description || '';
                agent.selectedTopic = linked.selectedTopic || '';
              }
            });
          }
        } catch (err) {
          console.error('[Agents] loading linked tags failed:', err);
        }

        if (myGeneration !== generation) return;
        list.sort((a, b) => (a.caseNumber || 0) - (b.caseNumber || 0));
        setAgents(list);
        setLoading(false);
      })();
    });

    return () => {
      generation += 1;
      unsubscribe();
    };
  }, [userId]);

  // Rules created before case numbers existed need one renumbering pass.
  useEffect(() => {
    if (agents.length > 0 && !agents[0].caseNumber) {
      httpsCallable(functions, 'updateAgentCaseNumbers')().catch((err) => {
        console.error('[Agents] renumbering failed:', err);
      });
    }
  }, [agents, functions]);

  // ── Processing switch ──────────────────────────────────────────────
  // `autoProcessEmails` is the backend gate. It is not a control here anymore:
  // each case has its own switch. Saving or turning a case on flips this on
  // so the matcher actually runs.
  useEffect(() => {
    if (!userId) return undefined;
    return onValue(
      ref(database, `/World/${userId}/settings/autoProcessEmails`),
      (snap) => setAutoProcess(snap.exists() ? !!snap.val() : false),
    );
  }, [userId]);

  const setAutoProcessTo = useCallback(async (next) => {
    try {
      await set(ref(database, `/World/${userId}/settings/autoProcessEmails`), next);
      return true;
    } catch (err) {
      console.error('[Agents] switching auto-process failed:', err);
      return false;
    }
  }, [userId]);

  const toggleAgentEnabled = useCallback(async (agent) => {
    if (!userId || !agent?.id) return;
    const next = agent.enabled === false;
    try {
      await update(ref(database, `/World/${userId}/settings/agents/${agent.id}`), {
        enabled: next,
        updatedAt: new Date().toISOString(),
      });
      const othersLive = agents.some((a) => a.id !== agent.id && a.enabled !== false);
      if (next && !autoProcess) await setAutoProcessTo(true);
      if (!next && !othersLive) await setAutoProcessTo(false);
    } catch (err) {
      console.error('[Agents] toggling case failed:', err);
      flash('error', `Could not switch “${agent.name}”.`);
    }
  }, [userId, flash, autoProcess, setAutoProcessTo, agents]);

  // Templates for the saved-reply picker. customTopics wins on the same key,
  // matching resolveSavedTemplate on the server.
  useEffect(() => {
    if (!userId) return undefined;

    const unsubSelected = onValue(
      ref(database, `/World/${userId}/settings/selectedTopics`),
      (snap) => setSelectedTopics(snap.exists() ? snap.val() : {}),
    );
    const unsubCustom = onValue(
      ref(database, `/World/${userId}/settings/customTopics`),
      (snap) => setCustomTopics(snap.exists() ? snap.val() : {}),
    );

    return () => { unsubSelected(); unsubCustom(); };
  }, [userId]);

  // ── Calendly meeting types ─────────────────────────────────────────
  const loadEventTypes = useCallback(async () => {
    setEventTypesLoading(true);
    setEventTypesError("");
    try {
      const settingsSnap = await get(ref(database, `/World/${userId}/settings/moreSettings`));
      const calendlyKey = settingsSnap.exists() ? settingsSnap.val().calendlyKey : null;

      if (!calendlyKey) {
        setEventTypesError('No Calendly API key yet. Add one under Admin → Integrations → Calendly.');
        return;
      }

      const result = await httpsCallable(functions, 'getCalendlyEventTypes')({ calendlyKey });
      if (result.data.success) {
        setEventTypes(result.data.eventTypes || []);
      } else {
        setEventTypesError(result.data.error || 'Could not load your meeting types.');
      }
    } catch (err) {
      console.error('[Agents] loading Calendly event types failed:', err);
      setEventTypesError(err.message || 'Could not load your meeting types.');
    } finally {
      setEventTypesLoading(false);
    }
  }, [userId, functions]);

  // ── Open / close the editor ────────────────────────────────────────
  const openFromTemplate = (template) => {
    setPickerOpen(false);
    const draft = templateToDraft(template);
    setEditor({ id: null, draft, initial: draft });
  };

  // Voice-driven case creation. Opens the SpeakEngine with the
  // 'inboxReplyCase' preset — user describes the situation out loud, AI
  // extracts every basic field (trigger, action, mood range, dos/donts,
  // tag), the AgentEditor then opens prefilled so the user still reviews
  // and saves deliberately. Nothing is persisted from this handler.
  const openFromSpeech = useCallback(() => {
    setPickerOpen(false);
    openCapture({
      preset: 'inboxReplyCase',
      onResult: async (values) => {
        // Guardrails — normalise every field the AgentEditor + backend
        // matcher care about, filling BLANK_AGENT defaults for anything
        // the AI left blank so the editor never sees an undefined value.
        const type = ['Reply', 'No Reply', 'Only Tag'].includes(values.type) ? values.type : 'Reply';
        const clampMood = (n, fallback) => {
          const num = Math.max(1, Math.min(10, Math.round(Number(n))));
          return Number.isFinite(num) ? num : fallback;
        };
        const happinessMin = clampMood(values.happinessMin, 1);
        const happinessMax = Math.max(happinessMin, clampMood(values.happinessMax, 10));
        const tag = (values.tag || '').trim().toLowerCase();
        const useTag = !!values.useTag || !!tag;

        const draft = {
          ...BLANK_AGENT,
          name: (values.name || '').trim() || 'New case',
          case: (values.case || '').trim(),
          type,
          task: type === 'Reply' ? ((values.task || '').trim()) : '',
          happinessMin,
          happinessMax,
          dos: Array.isArray(values.dos)   ? values.dos.filter(Boolean)   : [],
          donts: Array.isArray(values.donts) ? values.donts.filter(Boolean) : [],
          useTag,
          tag,
          tagColor: (values.tagColor && /^#[0-9a-fA-F]{6}$/.test(values.tagColor)) ? values.tagColor : '#1e40af',
          tagIcon: '',
          tagDescription: (values.tagDescription || '').trim(),
          selectedTopic: '',
          replyMode: 'instructions',
          replyTemplate: '',
          adherence: 'balanced',
        };
        setEditor({ id: null, draft, initial: draft });
        return 'Case drafted. Review and hit Save to activate.';
      },
    });
  }, [openCapture]);

  const openEdit = (agent) => {
    const draft = {
      ...BLANK_AGENT,
      ...agent,
      // Older rules predate several fields; fill the gaps so the controlled
      // inputs never flip between uncontrolled and controlled.
      task: agent.task || '',
      happinessMin: agent.happinessMin || 1,
      happinessMax: agent.happinessMax || 10,
      useCalendly: agent.useCalendly || false,
      selectedEventType: agent.selectedEventType || null,
      calendlyGuidelines: agent.calendlyGuidelines || '',
      useTag: agent.useTag || false,
      tag: agent.tag || '',
      tagColor: agent.tagColor || '#1e40af',
      tagIcon: agent.tagIcon || '',
      tagDescription: agent.tagDescription || '',
      selectedTopic: agent.selectedTopic || '',
      replyMode: agent.replyMode || 'instructions',
      replyTemplate: agent.replyTemplate || '',
      savedTemplateId: agent.savedTemplateId || '',
      savedTemplateVariantId: agent.savedTemplateVariantId || '',
      adherence: agent.adherence || 'balanced',
      enabled: agent.enabled !== false,
      dos: toList(agent.dos),
      donts: toList(agent.donts),
    };
    setEditor({ id: agent.id, draft, initial: draft });
  };

  const duplicate = (agent) => {
    const draft = { ...BLANK_AGENT, ...agent, id: undefined, name: `${agent.name} (copy)`, enabled: true };
    setEditor({ id: null, draft, initial: draft });
  };

  const editorDirty = !!editor
    && JSON.stringify(editor.draft) !== JSON.stringify(editor.initial);

  // Leaving a half-written case is almost always a misclick, so it asks — but
  // only when there is something to lose.
  const requestClose = () => {
    if (editorDirty) setPendingDiscard(true);
    else setEditor(null);
  };

  // The in-app exits are guarded above; this covers the reload and the closed
  // tab, which would otherwise take an unsaved case with them silently.
  useEffect(() => {
    if (!editorDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [editorDirty]);

  // ── Save ───────────────────────────────────────────────────────────
  // Writes exactly the shape the backend matcher expects; the payload is
  // identical for create and update apart from the timestamp field.
  const buildPayload = (draft) => {
    const isOnlyTag = draft.type === 'Only Tag';
    const isReply = draft.type === 'Reply';
    return {
      // Both texts are kept regardless of the active mode: switching back and
      // forth while writing must not silently throw away what was typed. Same
      // reasoning for the saved-template reference — flipping to "Describe it"
      // shouldn't forget which template you had picked.
      replyMode: isReply ? (draft.replyMode || 'instructions') : 'instructions',
      replyTemplate: isReply ? (draft.replyTemplate || '').trim() : '',
      savedTemplateId: isReply ? (draft.savedTemplateId || '') : '',
      savedTemplateVariantId: isReply ? (draft.savedTemplateVariantId || '') : '',
      adherence: isReply ? (draft.adherence || 'balanced') : 'balanced',
      dos: isReply ? toList(draft.dos) : [],
      donts: isReply ? toList(draft.donts) : [],
      name: draft.name.trim(),
      case: draft.case.trim(),
      type: draft.type,
      happinessMin: draft.happinessMin,
      happinessMax: draft.happinessMax,
      useCalendly: isOnlyTag ? false : (draft.useCalendly || false),
      selectedEventType: isOnlyTag ? null : (draft.selectedEventType || null),
      calendlyGuidelines: isOnlyTag ? '' : (draft.calendlyGuidelines || ''),
      useTag: isOnlyTag ? true : (draft.useTag || false),
      tag: (draft.tag || '').trim(),
      tagColor: draft.tagColor || '#1e40af',
      tagIcon: draft.tagIcon || '',
      tagDescription: (draft.tagDescription || '').trim(),
      selectedTopic: '',
      enabled: draft.enabled !== false,
      task: isReply ? (draft.task || '').trim() : null,
    };
  };

  const syncTag = async (agentId, payload, timestampField) => {
    const tagRef = ref(database, `/World/${userId}/settings/aiTags/${agentId}`);
    if (payload.useTag && payload.tag) {
      await set(tagRef, {
        tag: payload.tag,
        name: payload.name,
        color: payload.tagColor,
        icon: payload.tagIcon,
        description: payload.tagDescription,
        case: payload.case,
        overwriteOtherTags: true,
        selectedTopic: '',
        sourceAgentId: agentId,
        ...timestampField,
      });
    } else {
      await remove(tagRef);
    }
  };

  const handleSave = async () => {
    if (!editor) return;
    const payload = buildPayload(editor.draft);
    setSaving(true);

    try {
      if (editor.id) {
        await update(ref(database, `/World/${userId}/settings/agents/${editor.id}`), {
          ...payload,
          updatedAt: new Date().toISOString(),
        });
        await syncTag(editor.id, payload, { updatedAt: new Date().toISOString() });
        flash('ok', `“${payload.name}” updated.`);
      } else {
        const createdAt = new Date().toISOString();
        const newRef = push(ref(database, `/World/${userId}/settings/agents`));
        await set(newRef, { ...payload, caseNumber: agents.length + 1, createdAt });
        await syncTag(newRef.key, payload, { createdAt });

        // A saved case that never runs is not what anyone means by saving it.
        const turnedOn = !autoProcess && await setAutoProcessTo(true);
        flash('ok', turnedOn
          ? `“${payload.name}” is live. Inbox Reply is now running.`
          : `“${payload.name}” is live. It applies to the next incoming email.`);
      }

      setEditor(null);
      // Case numbers are what the matcher refers to, so they must stay dense
      // after every insert.
      httpsCallable(functions, 'updateAgentCaseNumbers')().catch(() => {});
    } catch (err) {
      console.error('[Agents] saving failed:', err);
      flash('error', `Could not save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    const agent = pendingDelete;
    setPendingDelete(null);
    if (!agent) return;

    try {
      await remove(ref(database, `/World/${userId}/settings/agents/${agent.id}`));
      await remove(ref(database, `/World/${userId}/settings/aiTags/${agent.id}`));
      const othersLive = agents.some((a) => a.id !== agent.id && a.enabled !== false);
      if (!othersLive) await setAutoProcessTo(false);
      flash('ok', `“${agent.name}” deleted.`);
      httpsCallable(functions, 'updateAgentCaseNumbers')().catch(() => {});
    } catch (err) {
      console.error('[Agents] deleting failed:', err);
      flash('error', `Could not delete: ${err.message}`);
    }
  };

  if (!userId) {
    return <p className="ags-guard">Please log in to manage your inbox replies.</p>;
  }

  const replyCount = agents.filter((a) => a.type === 'Reply').length;

  // The editor takes over the whole pane instead of floating above it: a case
  // is a long form, and a dialog that closes on a stray click loses all of it.
  if (editor) {
    return (
      <div className="ags ags--editor">
        <AgentEditor
          mode={editor.id ? 'edit' : 'create'}
          draft={editor.draft}
          onChange={(draft) => setEditor((prev) => ({ ...prev, draft }))}
          onCancel={requestClose}
          onSave={handleSave}
          saving={saving}
          userTopics={userTopics}
          takenNames={agents.filter((a) => a.id !== editor.id).map((a) => a.name || '')}
          calendly={{
            eventTypes,
            loading: eventTypesLoading,
            error: eventTypesError,
            onLoad: loadEventTypes,
          }}
        />

        <ConfirmModal
          isOpen={pendingDiscard}
          title="Discard this case?"
          message="You have unsaved changes. Leaving now throws them away."
          confirmText="Discard"
          cancelText="Keep editing"
          onConfirm={() => { setPendingDiscard(false); setEditor(null); }}
          onCancel={() => setPendingDiscard(false)}
        />
      </div>
    );
  }

  const seedCard = (
    <li
      className="ags-card ags-card--seed"
      role="button"
      tabIndex={0}
      onClick={() => openFromTemplate(null)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          openFromTemplate(null);
        }
      }}
    >
      <span className="ags-seed-plus" aria-hidden>
        <i className="fa fa-plus" />
      </span>
      <h4 className="ags-seed-title">Add a case</h4>
      <p className="ags-seed-copy">
        Every incoming email is matched to the case that fits and answered
        accordingly. Nothing is sent on its own. The reply waits in your inbox.
      </p>
      <p className="ags-seed-count">
        <strong>{agents.length}</strong> {agents.length === 1 ? 'case' : 'cases'}
        <span aria-hidden>·</span>
        <strong>{replyCount}</strong> that reply
      </p>
      <div className="ags-seed-actions">
        <button
          type="button"
          className="ags-speakBtn"
          onClick={(e) => { e.stopPropagation(); openFromSpeech(); }}
          title="Describe a case out loud. AI drafts the trigger, action, rules and tag for you."
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.95" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="9" y="2" width="6" height="12" rx="3" />
            <path d="M5 10a7 7 0 0 0 14 0" />
            <path d="M12 19v3" />
          </svg>
          Speak
        </button>
        <button type="button" className="ags-newBtn" onClick={(e) => { e.stopPropagation(); openFromTemplate(null); }}>
          <i className="fa fa-plus" /> New case
        </button>
      </div>
      <button type="button" className="ags-addAlt" onClick={(e) => { e.stopPropagation(); setPickerOpen(true); }}>
        or use a ready-made one
      </button>
    </li>
  );

  return (
    <div className="ags">
      {notice && <div className={`ags-notice ${notice.kind}`}>{notice.text}</div>}

      {loading ? (
        <div className="ags-skeleton">
          {[0, 1, 2, 3].map((i) => <div key={i} className="ags-skelRow" style={{ animationDelay: `${i * 0.12}s` }} />)}
        </div>
      ) : (
        <>
          <ul className="ags-list">
            {agents.map((agent, index) => {
              const meta = ACTION_META[agent.type] || ACTION_META.Reply;
              const usesTemplate = agent.type === 'Reply' && agent.replyMode === 'template';
              const usesSaved = agent.type === 'Reply' && agent.replyMode === 'saved';
              // Resolve the saved-template display name from userTopics — the
              // card stores an ID by design, so renaming stays reflected here.
              const savedTopic = usesSaved && agent.savedTemplateId ? userTopics[agent.savedTemplateId] : null;
              const savedName = savedTopic?.topic || (usesSaved ? '(template removed)' : '');
              const adherenceId = usesSaved
                ? (savedTopic?.aiAdherence || 'exact')
                : agent.adherence;
              const level = ADHERENCE_LEVELS.find((l) => l.id === adherenceId)
                || (adherenceId === 'creative' ? { id: 'creative', short: 'Creative' } : null);
              const ruleCount = toList(agent.dos).length + toList(agent.donts).length;
              const agentOn = agent.enabled !== false;
              const thenText = agent.type === 'Reply'
                ? (usesSaved
                  ? `Uses template “${savedName}”.`
                  : usesTemplate
                    ? agent.replyTemplate
                    : agent.task)
                : agent.type === 'No Reply'
                  ? 'Leave the email unanswered.'
                  : 'Only label the email.';
              return (
                <li
                  key={agent.id}
                  className={`ags-card ${agentOn ? '' : 'ags-card--off'}`}
                  onClick={() => openEdit(agent)}
                >
                  <div className="ags-card-header">
                    <div className="ags-card-info">
                      <span className={`ags-card-badge ${meta.tone}`}>
                        <i className={`fa ${meta.icon}`} />
                        {meta.label}
                      </span>
                      <h4 className="ags-card-name">{agent.name}</h4>
                    </div>
                    <div className="ags-card-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        className={`ags-card-switch ${agentOn ? 'on' : ''}`}
                        onClick={() => toggleAgentEnabled(agent)}
                        aria-pressed={agentOn}
                        aria-label={agentOn ? `Turn “${agent.name}” off` : `Turn “${agent.name}” on`}
                        title={agentOn ? 'This case is on' : 'This case is off'}
                      >
                        <span className="ags-card-knob" />
                      </button>
                      <button type="button" onClick={() => openEdit(agent)} title="Edit case" aria-label="Edit case">
                        <i className="fa fa-pen" />
                      </button>
                      <button type="button" onClick={() => duplicate(agent)} title="Duplicate case" aria-label="Duplicate case">
                        <i className="fa fa-clone" />
                      </button>
                      <button type="button" className="danger" onClick={() => setPendingDelete(agent)} title="Delete case" aria-label="Delete case">
                        <i className="fa fa-trash" />
                      </button>
                    </div>
                  </div>

                  <div className="ags-card-flow">
                    <div className="ags-card-step">
                      <span className="ags-kw">When</span>
                      <span className="ags-card-text">{agent.case}</span>
                    </div>
                    <div className="ags-card-step">
                      <span className="ags-kw">Then</span>
                      <span className="ags-card-text">{thenText}</span>
                    </div>
                  </div>

                  <div className="ags-card-meta">
                    <span className={`ags-pill ${agentOn ? 'ags-pill-live' : 'ags-pill-paused'}`}>
                      {agentOn ? 'Live' : 'Off'}
                    </span>
                    <span className="ags-pill">{describeRating(agent.happinessMin, agent.happinessMax)}</span>
                    {usesTemplate && (
                      <span className="ags-pill">
                        <i className="fa fa-file-lines" /> Your text{level ? ` · ${level.short}` : ''}
                      </span>
                    )}
                    {usesSaved && (
                      <span className="ags-pill" title={savedName}>
                        <i className="fa fa-book-bookmark" /> Template{level ? ` · ${level.short}` : ''}
                      </span>
                    )}
                    {ruleCount > 0 && (
                      <span className="ags-pill">
                        <i className="fa fa-shield-halved" /> {ruleCount} {ruleCount === 1 ? 'rule' : 'rules'}
                      </span>
                    )}
                    {agent.useTag && agent.tag && (
                      <span className="ags-pill ags-pill-tag" style={{ backgroundColor: agent.tagColor || '#1e40af' }}>
                        {agent.tagIcon && <img src={agent.tagIcon} alt="" />}
                        {agent.tag}
                      </span>
                    )}
                    {agent.useCalendly && <span className="ags-pill"><i className="fa fa-calendar-check" /> Calendly</span>}
                    <span className="ags-card-order">Case {agent.caseNumber || index + 1}</span>
                  </div>
                </li>
              );
            })}
            {seedCard}
          </ul>
        </>
      )}

      {pickerOpen && (
        <AgentTemplatePicker
          variant="modal"
          onPick={openFromTemplate}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <ConfirmModal
        isOpen={!!pendingDelete}
        title="Delete this case?"
        message={pendingDelete
          ? `“${pendingDelete.name}” will stop handling incoming emails. This cannot be undone.`
          : ''}
        confirmText="Delete case"
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
};

export default AgentsSettings;
